import { DndProvider } from "react-dnd";
import { useTranslation } from "react-i18next";

import { useState } from "react";

import { CircularProgress } from "@mui/material";
import { faPlus } from "@fortawesome/pro-regular-svg-icons";
import { HTML5Backend } from "react-dnd-html5-backend";

import { Icon } from "@/components";
import { Button } from "@/components/Button/Button";
import DeleteModal from "@/components/Modals/DeleteModal";

import {
	useCreateCompanyRevenueMutation,
	useDeleteCompanyRevenueMutation,
} from "@/pages/Private/redux/company-revenue/company-revenue.api";

import { ButtonColor } from "@/components/Button/types";

import {
	CompanyRevenue,
	CompanyRevenueArray,
	CreateCompanyRevenue,
	CreateCompanyRevenueSchema,
} from "../schema/company-revenue";
import EditModeCompanyRevenueItem from "./EditableCompanyRevenueItem/EditableCompanyRevenueItem";
import EditableCompanyRevenueItem from "./EditableCompanyRevenueItem";

interface EditableCompanyRevenueListProps {
	data?: CompanyRevenueArray;
	isLoading: boolean;
	editingCompanyRevenueId: number;
	setEditingCompanyRevenueId: (value: number) => void;
}

export default function EditableCompanyRevenueList({
	data,
	isLoading,
	editingCompanyRevenueId,
	setEditingCompanyRevenueId,
}: EditableCompanyRevenueListProps) {
	const { t } = useTranslation();
	const ts = (key: string, options = {}) => t(`manage-company-revenue.${key}`, options);
	const [isCreating, setCreating] = useState(0);

	const [deletingCompanyRevenue, setDeletingCompanyRevenue] = useState<CompanyRevenue | null>(null);
	const [createCompanyRevenue, { isLoading: isCompanyRevenueCreating }] =
		useCreateCompanyRevenueMutation();
	const [deleteCompanyRevenue, { isLoading: isCompanyRevenueDeleting }] =
		useDeleteCompanyRevenueMutation();

	const handleCreateCompanyRevenue = async (values: CreateCompanyRevenue) => {
		try {
			await createCompanyRevenue(values).unwrap();
		} catch (error) {
			console.error(error);
		} finally {
			setCreating(0);
		}
	};

	const handleDeleteCompanyRevenue = async () => {
		if (!deletingCompanyRevenue?.id) {
			return;
		}

		try {
			await deleteCompanyRevenue(deletingCompanyRevenue?.id ?? 0).unwrap();
			setDeletingCompanyRevenue(null);
		} catch (err) {
			console.error(err);
		}
	};

	const onEditCompanyRevenue = (id: number) => {
		setEditingCompanyRevenueId(id);
		setCreating(0);
	};

	const handleCancel = () => {
		setEditingCompanyRevenueId(-1);
		setCreating(0);
	};

	return (
		<DndProvider backend={HTML5Backend}>
			<div
				className={`w-full border border-primary-gray-lightest p-1 pt-4 rounded-16 space-y-4 bg-surface`}
			>
				<div className="flex justify-between items-center px-3">
					<h3 className="w-full flex gap-2 text-primary-blue-dark text-[14px] font-medium">
						{ts("company-revenues")}

						<span className="border border-primary-gray-lightest bg-primary-gray-lightest rounded-[4px] w-5 h-5 flex items-center justify-center text-text text-[12px] font-medium">
							{data?.length ?? "0"}
						</span>
					</h3>
					<Button
						className="!min-w-5 !min-h-5 !max-w-5 !max-h-5 !p-0"
						color={ButtonColor.ACTION}
						image={<Icon className="w-3 h-4 text-white" icon={faPlus} />}
						title=""
						type="button"
						onClick={() => {
							setCreating(1);
							if (editingCompanyRevenueId !== -1) {
								setEditingCompanyRevenueId(-1);
							}
						}}
					/>
				</div>
				{isLoading ? (
					<div className="w-full flex justify-center py-10">
						<CircularProgress className="!w-12 !h-12" />
					</div>
				) : (
					<div className="space-y-1 relative">
						{isCompanyRevenueDeleting && (
							<div className="w-full h-full absolute flex justify-center py-12 bg-white opacity-50">
								<CircularProgress className="!w-12 !h-12" />
							</div>
						)}
						{isCreating === 1 && (
							<EditModeCompanyRevenueItem
								data={{ value: "" }}
								isLoading={isCompanyRevenueCreating}
								setEditing={handleCancel}
								validationSchema={CreateCompanyRevenueSchema}
								onSubmit={handleCreateCompanyRevenue}
							/>
						)}
						{data?.length ? (
							data?.map((item) => (
								<EditableCompanyRevenueItem
									key={item?.id}
									data={item}
									isEditing={editingCompanyRevenueId === item?.id}
									isEditingInProcess={isCreating !== 0 || editingCompanyRevenueId !== -1}
									setDeletingCompanyRevenue={setDeletingCompanyRevenue}
									setEditing={onEditCompanyRevenue}
								/>
							))
						) : (
							<p className="text-[16px] text-subtle text-center py-4">{ts("list-empty")}</p>
						)}

						{isCreating === -1 && (
							<EditModeCompanyRevenueItem
								data={{ value: "" }}
								isLoading={isCompanyRevenueCreating}
								setEditing={handleCancel}
								validationSchema={CreateCompanyRevenueSchema}
								onSubmit={handleCreateCompanyRevenue}
							/>
						)}

						<Button
							className="!text-[14px] !m-3 !mt-4 !w-fit !font-medium leading-6"
							image={<Icon className="text-white w-5 h-5 mr-2" icon={faPlus} />}
							title={ts("add-company-revenue")}
							type="button"
							onClick={() => {
								setCreating(-1);
								if (editingCompanyRevenueId !== -1) {
									setEditingCompanyRevenueId(-1);
								}
							}}
						/>
					</div>
				)}
			</div>
			<DeleteModal
				description={ts("delete-description", { revenueValue: deletingCompanyRevenue?.value })}
				handleClose={() => setDeletingCompanyRevenue(null)}
				handleDelete={handleDeleteCompanyRevenue}
				isLoading={isCompanyRevenueDeleting}
				isShowing={!!deletingCompanyRevenue}
				title={ts("delete-title")}
			/>
		</DndProvider>
	);
}
